import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient, handleApiError } from '@/infrastructure/api/client'
import type {
  SiteIdentitySettings,
  SocialSettings,
  BotSettings,
  BreakingNewsSettings,
} from '@/domains/types'

export const settingsKeys = {
  siteIdentity: (language: string) => ['settings', 'site-identity', language] as const,
  social: ['settings', 'social'] as const,
  bot: ['settings', 'bot'] as const,
  breaking: (language: string) => ['settings', 'breaking', language] as const,
}

export function useGetSiteIdentity(language: string) {
  return useQuery({
    queryKey: settingsKeys.siteIdentity(language),
    queryFn: async (): Promise<SiteIdentitySettings> => {
      const response = await apiClient.get<SiteIdentitySettings>('/settings/site-identity', {
        params: { lang: language },
      })
      return response.data
    },
    staleTime: 1000 * 60 * 5,
  })
}

export function useUpdateSiteIdentity(language: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (
      data: Partial<Omit<SiteIdentitySettings, 'updatedAt'>>,
    ): Promise<SiteIdentitySettings> => {
      const response = await apiClient.patch<SiteIdentitySettings>(
        '/settings/site-identity',
        data,
        { params: { lang: language } },
      )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: settingsKeys.siteIdentity(language) })
    },
    onError: (error) => {
      throw new Error(handleApiError(error))
    },
  })
}

export function useGetSocial() {
  return useQuery({
    queryKey: settingsKeys.social,
    queryFn: async (): Promise<SocialSettings> => {
      const response = await apiClient.get<SocialSettings>('/settings/social')
      // Guarantee `footerLinks` is an array even when the payload is partial.
      return {
        ...response.data,
        footerLinks: Array.isArray(response.data?.footerLinks) ? response.data.footerLinks : [],
      }
    },
    staleTime: 1000 * 60 * 5,
  })
}

export function useUpdateSocial() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (
      data: Partial<Omit<SocialSettings, 'updatedAt'>>,
    ): Promise<SocialSettings> => {
      const response = await apiClient.patch<SocialSettings>('/settings/social', data)
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: settingsKeys.social })
    },
    onError: (error) => {
      throw new Error(handleApiError(error))
    },
  })
}

export function useGetBotSettings() {
  return useQuery({
    queryKey: settingsKeys.bot,
    queryFn: async (): Promise<BotSettings> => {
      const response = await apiClient.get<BotSettings>('/settings/bot')
      return response.data
    },
    staleTime: 1000 * 60 * 5,
  })
}

export function useGetBreakingNews(language: string) {
  return useQuery({
    queryKey: settingsKeys.breaking(language),
    queryFn: async (): Promise<BreakingNewsSettings> => {
      const response = await apiClient.get<BreakingNewsSettings>('/settings/breaking', {
        params: { lang: language },
      })
      return response.data
    },
    staleTime: 1000 * 60 * 5,
  })
}

export function useUpdateBreakingNews(language: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (
      data: Partial<Omit<BreakingNewsSettings, 'updatedAt' | 'investigationSlugs'>>,
    ): Promise<BreakingNewsSettings> => {
      const response = await apiClient.patch<BreakingNewsSettings>('/settings/breaking', data, {
        params: { lang: language },
      })
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: settingsKeys.breaking(language) })
    },
    onError: (error) => {
      throw new Error(handleApiError(error))
    },
  })
}

export function useUpdateBotSettings() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: Partial<BotSettings>): Promise<BotSettings> => {
      const response = await apiClient.patch<BotSettings>('/settings/bot', data)
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: settingsKeys.bot })
    },
    onError: (error) => {
      throw new Error(handleApiError(error))
    },
  })
}
